import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { CardShell, asText } from './shared';

export interface TableSpec {
  tipo: 'tabela';
  titulo?: string;
  colunas?: unknown[];
  linhas?: unknown[][];
  fonte?: string;
}

// Acima disto aparece o filtro; a tabela mostra PAGE linhas e um "ver todas".
const FILTER_FROM = 8;
const PAGE = 10;

// Célula "numérica": um número, com R$ ou uma unidade curta ("75 mg", "12%",
// "R$ 1.234,56"). Texto que só contém números ("37,5 mg por 4–7 dias") não é.
const NUMERIC_CELL = /^\s*(R\$\s*)?([-+]?\d{1,3}(\.\d{3})+(,\d+)?|[-+]?\d+([.,]\d+)?)\s*(%|[A-Za-zÀ-ÿµ°/]{1,8})?\s*$/;
export const cellNumber = (cell: string) => {
  const m = NUMERIC_CELL.exec(cell);
  if (!m) return NaN;
  // pt-BR: "1.234" e "1.234,5" têm ponto de milhar; "37,5" vírgula decimal.
  const g = m[3] ? m[2].replace(/\./g, '').replace(',', '.') : m[2].replace(',', '.');
  return parseFloat(g);
};

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function DataTable({ spec }: { spec: TableSpec }) {
  const cols = (spec.colunas || []).map(asText);
  const rows = useMemo(
    () => (spec.linhas || []).filter(Array.isArray).map((r) => cols.map((_, c) => asText(r[c]))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [spec.linhas, cols.length],
  );
  const [sort, setSort] = useState<{ col: number; dir: 1 | -1 } | null>(null);
  const [query, setQuery] = useState('');
  const [all, setAll] = useState(false);

  // Coluna numérica (todas as células com número) ordena por valor, não por texto.
  const numeric = useMemo(() => cols.map((_, c) => rows.some((r) => r[c] !== '')
    && rows.every((r) => r[c] === '' || Number.isFinite(cellNumber(r[c])))), [cols, rows]);

  const shown = useMemo(() => {
    let out = rows;
    if (query.trim()) {
      const q = norm(query.trim());
      out = out.filter((r) => r.some((cell) => norm(cell).includes(q)));
    }
    if (sort) {
      const { col, dir } = sort;
      out = [...out].sort((a, b) => {
        if (numeric[col]) {
          return dir * ((cellNumber(a[col]) || 0) - (cellNumber(b[col]) || 0));
        }
        return dir * a[col].localeCompare(b[col], 'pt-BR', { numeric: true });
      });
    }
    return out;
  }, [rows, query, sort, numeric]);

  if (!cols.length) return null;
  const visible = all ? shown : shown.slice(0, PAGE);
  const toggleSort = (c: number) => setSort((s) => (s && s.col === c
    ? (s.dir === 1 ? { col: c, dir: -1 } : null)
    : { col: c, dir: 1 }));

  return (
    <CardShell title={spec.titulo} source={spec.fonte} label="tabela">
      {rows.length > FILTER_FROM && (
        <Filter
          type="search"
          placeholder={`Filtrar ${rows.length} linhas…`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Filtrar a tabela"
        />
      )}
      <Scroll>
        <Table>
          <thead>
            <tr>
              {cols.map((c, i) => (
                <th key={i} aria-sort={sort?.col === i ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
                  <SortButton type="button" onClick={() => toggleSort(i)} $numeric={numeric[i]}>
                    {c}
                    <SortIcon $active={sort?.col === i}>
                      {sort?.col === i && sort.dir === -1
                        ? <ArrowDownwardRoundedIcon style={{ fontSize: 13 }} />
                        : <ArrowUpwardRoundedIcon style={{ fontSize: 13 }} />}
                    </SortIcon>
                  </SortButton>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((r, i) => (
              <tr key={i}>
                {r.map((cell, c) => <Td key={c} $numeric={numeric[c]}>{cell}</Td>)}
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><Td colSpan={cols.length} $numeric={false}>Nenhuma linha com “{query}”.</Td></tr>
            )}
          </tbody>
        </Table>
      </Scroll>
      {!all && shown.length > PAGE && (
        <More type="button" onClick={() => setAll(true)}>Ver todas as {shown.length} linhas</More>
      )}
    </CardShell>
  );
}

export default DataTable;

const Filter = styled.input`
  width: 100%;
  max-width: 280px;
  box-sizing: border-box;
  margin-bottom: 10px;
  padding: 6px 10px;
  border-radius: 8px;
  font: inherit;
  font-size: 0.82rem;
  color: inherit;
  background: transparent;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.15)')};
  &:focus { outline: none; border-color: ${({ theme }) => theme.prompt_accent_color || '#1a3c34'}; }
`;

const Scroll = styled.div`
  overflow-x: auto;
  margin: 0 -4px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  line-height: 1.45;

  th {
    text-align: left;
    padding: 0;
    border-bottom: 1.5px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.14)')};
    white-space: nowrap;
  }
  tbody tr:nth-child(even) {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.02)')};
  }
  tbody tr:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)')};
  }
`;

const SortButton = styled.button<{ $numeric: boolean }>`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: ${({ $numeric }) => ($numeric ? 'flex-end' : 'flex-start')};
  gap: 4px;
  width: 100%;
  padding: 8px 10px;
  font-weight: 600;
  cursor: pointer;
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.prompt_accent_color || '#1a3c34'}; border-radius: 4px; }
`;

const SortIcon = styled.span<{ $active: boolean }>`
  display: inline-flex;
  opacity: ${({ $active }) => ($active ? 0.9 : 0)};
  ${SortButton}:hover & { opacity: ${({ $active }) => ($active ? 0.9 : 0.35)}; }
`;

const Td = styled.td<{ $numeric: boolean }>`
  padding: 7px 10px;
  vertical-align: top;
  text-align: ${({ $numeric }) => ($numeric ? 'right' : 'left')};
  font-variant-numeric: ${({ $numeric }) => ($numeric ? 'tabular-nums' : 'normal')};
  border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)')};
`;

const More = styled.button`
  all: unset;
  margin-top: 8px;
  font-size: 0.8rem;
  cursor: pointer;
  color: ${({ theme }) => theme.prompt_accent_color || '#1a3c34'};
  &:hover { text-decoration: underline; }
`;
